import { useEffect, useMemo, useRef } from "react";
import { Outlet, useLocation, useParams } from "react-router-dom";
import { usePublicSite } from "../../hooks/usePublicSite";
import { applyBrandColor } from "../../utils/ThemeColor";
import { resolveImageUrl } from "../../utils/ImageUrl";
import { Constants } from "../../constants/constants";
import Seo from "../../components/Seo";
import WhatsAppFloatButton from "../../components/WhatsAppFloatButton";
import { PublicSiteContext } from "./PublicSiteContext";
import { StagedImageProvider } from "./StagedImageLoading";
import PublicHeader from "./PublicHeader";
import PublicFooter from "./PublicFooter";
import "./PublicSite.css";

/** Meta descriptions work best under ~160 chars. */
const truncate = (text: string, max = 160): string =>
  text.length <= max ? text : `${text.slice(0, max - 1).trimEnd()}…`;

const PublicSiteLayout = () => {
  const { slug } = useParams<{ slug: string }>();
  const { website, loading, error, notFound, reload } = usePublicSite(slug);
  const location = useLocation();
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (rootRef.current)
      applyBrandColor(
        rootRef.current,
        website?.settings?.brandColor ?? undefined,
      );
  }, [website?.settings?.brandColor]);

  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  }, [location]);

  const org = website?.organization;

  const shareImage = resolveImageUrl(org?.logoUrl ?? org?.bannerUrl) ?? null;

  const seo = useMemo(() => {
    if (!org) return null;
    const description = truncate(
      org.description ??
        org.tagline ??
        `${org.name} — services, appointments and contact on ${Constants.SITE_NAME}.`,
    );
    const sameAs = [
      org.facebookUrl,
      org.instagramUrl,
      org.youtubeUrl,
      org.linkedinUrl,
      org.tiktokUrl,
    ].filter((url): url is string => Boolean(url));
    return {
      title: `${org.name} — ${Constants.SITE_NAME}`,
      description,
      image: shareImage,
      jsonLd: {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: org.name,
        url: `${Constants.SITE_URL}/${org.slug}`,
        ...(shareImage && { logo: shareImage, image: shareImage }),
        description,
        ...(org.phone && { telephone: org.phone }),
        ...(org.email && { email: org.email }),
        ...(sameAs.length > 0 && { sameAs }),
      },
    };
  }, [org, shareImage]);

  const contactPhone = org?.phone;

  return (
    <PublicSiteContext.Provider value={website}>
      <div ref={rootRef} className="psite">
        {notFound ? (
          <Seo
            title={`Website not found — ${Constants.SITE_NAME}`}
            noIndex
          />
        ) : (
          seo && (
            <Seo
              title={seo.title}
              description={seo.description}
              canonicalPath={location.pathname}
              image={seo.image}
              jsonLd={seo.jsonLd}
            />
          )
        )}
        <PublicHeader />

        {loading ? (
          <div className="psite-status">Loading website...</div>
        ) : notFound ? (
          <div className="psite-status">
            <h1 className="psite-status__title">Website not found</h1>
            <p>
              The page “{slug}” does not exist or isn’t published. Please check
              the link or contact the organization.
            </p>
          </div>
        ) : error || !website ? (
          <div className="psite-status">
            <p>{error || "Failed to load this website."}</p>
            <button
              className="psite-btn psite-btn--primary"
              onClick={reload}
            >
              Retry
            </button>
          </div>
        ) : (
          <main className="psite-main">
            <StagedImageProvider key={location.pathname}>
              <Outlet />
            </StagedImageProvider>
          </main>
        )}

        <WhatsAppFloatButton phone={contactPhone} />

        <PublicFooter />
      </div>
    </PublicSiteContext.Provider>
  );
};

export default PublicSiteLayout;
